import { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Button, CircularProgress, Paper, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Alert,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  Calculate as CalcIcon, Save as SaveIcon, Visibility as ViewIcon,
} from '@mui/icons-material';
import { UtilidadesService } from '../services/utilidadesService';
import type { Utilidades, UtilidadesDetalle } from '../types/utilidades';

const fmt = (n: number) => `S/ ${n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function UtilidadesPage() {
  const [anio, setAnio] = useState(new Date().getFullYear() - 1);
  const [utilidadEmpresa, setUtilidadEmpresa] = useState(0);
  const [porcentaje, setPorcentaje] = useState(10);
  const [preview, setPreview] = useState<Utilidades | null>(null);
  const [historico, setHistorico] = useState<Utilidades[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState<UtilidadesDetalle[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => { loadHistorico(); }, []);

  const loadHistorico = async () => {
    try {
      const data = await UtilidadesService.getAll();
      setHistorico(data);
    } catch { /* handled */ }
  };

  const handleCalcular = async () => {
    if (utilidadEmpresa <= 0) { setMessage('Ingrese la utilidad de la empresa'); return; }
    setLoading(true);
    setMessage(null);
    try {
      const result = await UtilidadesService.calcular({ anio, utilidadEmpresa, porcentajeReparto: porcentaje });
      setPreview(result);
    } catch { /* handled */ }
    finally { setLoading(false); }
  };

  const handleGrabar = async () => {
    if (utilidadEmpresa <= 0) return;
    setLoading(true);
    try {
      await UtilidadesService.grabar({ anio, utilidadEmpresa, porcentajeReparto: porcentaje });
      setMessage('Utilidades grabadas correctamente');
      setPreview(null);
      await loadHistorico();
    } catch { /* handled */ }
    finally { setLoading(false); }
  };

  const handleViewDetail = async (id: number) => {
    try {
      const data = await UtilidadesService.getById(id);
      setSelectedDetail(data.detalles ?? []);
      setDetailDialog(true);
    } catch { /* handled */ }
  };

  const historicoColumns: GridColDef[] = [
    { field: 'anio', headerName: 'Año', width: 80 },
    { field: 'utilidadEmpresa', headerName: 'Utilidad Empresa', width: 150, valueFormatter: (v) => fmt(v) },
    { field: 'porcentajeReparto', headerName: '% Reparto', width: 100, valueFormatter: (v) => `${v}%` },
    { field: 'montoRepartir', headerName: 'Monto a Repartir', width: 150, valueFormatter: (v) => fmt(v) },
    { field: 'cantidadTrabajadores', headerName: 'Trabajadores', width: 110, type: 'number' },
    { field: 'estado', headerName: 'Estado', width: 110,
      renderCell: (p) => <Chip label={p.value} size="small" color={p.value === 'PAGADA' ? 'success' : 'default'} /> },
    { field: 'acciones', headerName: '', width: 80, sortable: false,
      renderCell: (p) => (
        <Button size="small" startIcon={<ViewIcon />} onClick={() => handleViewDetail(p.row.idUtilidades)}>
          Ver
        </Button>
      )},
  ];

  const previewColumns: GridColDef[] = [
    { field: 'nombre', headerName: 'Trabajador', flex: 1, minWidth: 200,
      valueGetter: (_v, row) => row.trabajador?.personal?.personaContacto ?? `#${row.idTrabajador}` },
    { field: 'diasLaborados', headerName: 'Días', width: 70, type: 'number' },
    { field: 'remuneracionAnual', headerName: 'Rem. Anual', width: 130, valueFormatter: (v) => fmt(v) },
    { field: 'partePorDias', headerName: 'Parte Días', width: 120, valueFormatter: (v) => fmt(v) },
    { field: 'partePorRemuneraciones', headerName: 'Parte Rem.', width: 120, valueFormatter: (v) => fmt(v) },
    { field: 'totalUtilidades', headerName: 'Total Util.', width: 120, valueFormatter: (v) => fmt(v) },
    { field: 'renta5ta', headerName: 'Renta 5ta', width: 100, valueFormatter: (v) => fmt(v) },
    { field: 'netoPagar', headerName: 'Neto', width: 120, valueFormatter: (v) => fmt(v) },
  ];

  const detailColumns: GridColDef[] = [
    { field: 'nombre', headerName: 'Trabajador', flex: 1, minWidth: 200,
      valueGetter: (_v, row) => row.trabajador?.personal?.personaContacto ?? `#${row.idTrabajador}` },
    { field: 'diasLaborados', headerName: 'Días', width: 70, type: 'number' },
    { field: 'remuneracionAnual', headerName: 'Rem. Anual', width: 120, valueFormatter: (v) => fmt(v) },
    { field: 'partePorDias', headerName: 'Parte Días', width: 110, valueFormatter: (v) => fmt(v) },
    { field: 'partePorRemuneraciones', headerName: 'Parte Rem.', width: 110, valueFormatter: (v) => fmt(v) },
    { field: 'totalUtilidades', headerName: 'Total', width: 110, valueFormatter: (v) => fmt(v) },
    { field: 'renta5ta', headerName: 'Renta 5ta', width: 100, valueFormatter: (v) => fmt(v) },
    { field: 'netoPagar', headerName: 'Neto', width: 110, valueFormatter: (v) => fmt(v) },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>Reparto de Utilidades</Typography>

      {message && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}

      {/* Formulario de cálculo */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Calcular Utilidades</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField label="Año" type="number" size="small" sx={{ width: 100 }}
            value={anio} onChange={(e) => setAnio(Number(e.target.value))} />
          <TextField label="Utilidad de la Empresa (S/)" type="number" size="small" sx={{ width: 250 }}
            value={utilidadEmpresa || ''} onChange={(e) => setUtilidadEmpresa(Number(e.target.value))} />
          <TextField label="% Reparto" type="number" size="small" sx={{ width: 100 }}
            value={porcentaje} onChange={(e) => setPorcentaje(Number(e.target.value))} />
          <Button variant="contained" startIcon={loading ? <CircularProgress size={18} /> : <CalcIcon />}
            onClick={handleCalcular} disabled={loading}>
            Calcular
          </Button>
          {preview && (
            <Button variant="contained" color="success" startIcon={<SaveIcon />}
              onClick={handleGrabar} disabled={loading}>
              Grabar
            </Button>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Ley 892: 50% proporcional a días trabajados + 50% proporcional a remuneraciones percibidas
        </Typography>
      </Paper>

      {/* Preview del cálculo */}
      {preview && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Resultado del Cálculo - {preview.anio}</Typography>
          <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Monto a Repartir</Typography>
              <Typography variant="h6" color="primary">{fmt(preview.montoRepartir)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">50% por Días</Typography>
              <Typography variant="h6">{fmt(preview.montoPorDias)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">50% por Remuneraciones</Typography>
              <Typography variant="h6">{fmt(preview.montoPorRemuneraciones)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Trabajadores</Typography>
              <Typography variant="h6">{preview.cantidadTrabajadores}</Typography>
            </Box>
          </Stack>
          <DataGrid
            rows={preview.detalles?.map((d, i) => ({ ...d, _id: i })) ?? []}
            columns={previewColumns}
            getRowId={(row) => row._id}
            autoHeight
            density="compact"
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        </Paper>
      )}

      {/* Histórico */}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Histórico de Repartos</Typography>
      <DataGrid
        rows={historico}
        columns={historicoColumns}
        getRowId={(row) => row.idUtilidades}
        autoHeight
        density="compact"
        disableRowSelectionOnClick
        pageSizeOptions={[10]}
      />

      {/* Dialog detalle */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Detalle de Utilidades</DialogTitle>
        <DialogContent>
          <DataGrid
            rows={selectedDetail.map((d, i) => ({ ...d, _id: i }))}
            columns={detailColumns}
            getRowId={(row) => row._id}
            autoHeight
            density="compact"
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
